export type TenantId = 'quantleap' | 'zebu';

interface TenantConfig {
  id: TenantId;
  name: string;
  tagline: string;
  metaTitle: string;
  metaDescription: string;
  primaryColor: string;
  logoIcon: string;
  defaultBroker: string;
  showRegistration: boolean;
  showMultiBroker: boolean;
}

const tenants: Record<TenantId, TenantConfig> = {
  quantleap: {
    id: 'quantleap',
    name: 'Quantleap Analytics',
    tagline: 'Algorithmic Trading Platform for Indian Markets',
    metaTitle: 'Quantleap Analytics - Algo Trading',
    metaDescription: 'Quantleap Analytics - Algorithmic Trading Platform for Indian Markets',
    primaryColor: 'blue',
    logoIcon: 'Turtle',
    defaultBroker: '',
    showRegistration: true,
    showMultiBroker: true,
  },
  zebu: {
    id: 'zebu',
    name: 'Zebu Algo Trading',
    tagline: 'Algorithmic Trading Powered by Zebu',
    metaTitle: 'Zebu Algo – Retail Algo Trading Platform for Options & Stocks',
    metaDescription: 'Zebu Algo is a SEBI-compliant retail algo trading platform enabling automated options and stock strategies with a strategy builder and exchange-approved strategy execution.',
    primaryColor: 'zebu',
    logoIcon: 'TrendingUp',
    defaultBroker: 'zebu',
    showRegistration: false,
    showMultiBroker: false,
  },
};

export const getTenantConfig = (): TenantConfig =>
  tenants[(process.env.REACT_APP_TENANT || 'quantleap') as TenantId] || tenants.quantleap;

export const isWhiteLabel = (): boolean => getTenantConfig().id !== 'quantleap';

export const isZebu = (): boolean => getTenantConfig().id === 'zebu';

export const ZEBU_SIGNUP_URL = 'https://oa.zebuetrade.com/?ref=zalgo';

interface BrandClasses {
  activeBg: string;       // e.g. active toggle / pagination button
  activeTab: string;      // e.g. active underline tab
  selectionBar: string;   // e.g. row selection bar background + border
  selectionText: string;  // e.g. text inside selection bar
  sortIcon: string;       // e.g. active sort arrow
  link: string;           // e.g. text links / "View All" links
  linkHover: string;      // e.g. link hover state
  iconText: string;       // e.g. icon color inside containers
  iconBg: string;         // e.g. icon container background
}

const brandClassMap: Record<string, BrandClasses> = {
  blue: {
    activeBg: 'bg-blue-600 text-white',
    activeTab: 'border-blue-600 text-blue-600 dark:text-blue-400',
    selectionBar: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800',
    selectionText: 'text-blue-700 dark:text-blue-300',
    sortIcon: 'text-blue-500',
    link: 'text-blue-600 dark:text-blue-400',
    linkHover: 'hover:text-blue-700 dark:hover:text-blue-300',
    iconText: 'text-blue-600',
    iconBg: 'bg-blue-50 dark:bg-blue-900/20',
  },
  emerald: {
    activeBg: 'bg-emerald-600 text-white',
    activeTab: 'border-emerald-600 text-emerald-600 dark:text-emerald-400',
    selectionBar: 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800',
    selectionText: 'text-emerald-700 dark:text-emerald-300',
    sortIcon: 'text-emerald-500',
    link: 'text-emerald-600 dark:text-emerald-400',
    linkHover: 'hover:text-emerald-700 dark:hover:text-emerald-300',
    iconText: 'text-emerald-600',
    iconBg: 'bg-emerald-50 dark:bg-emerald-900/20',
  },
  zebu: {
    activeBg: 'bg-zebu-600 text-white',
    activeTab: 'border-zebu-600 text-zebu-600 dark:text-zebu-400',
    selectionBar: 'bg-zebu-50 dark:bg-zebu-900/20 border-zebu-200 dark:border-zebu-800',
    selectionText: 'text-zebu-700 dark:text-zebu-300',
    sortIcon: 'text-zebu-500',
    link: 'text-zebu-600 dark:text-zebu-400',
    linkHover: 'hover:text-zebu-700 dark:hover:text-zebu-300',
    iconText: 'text-zebu-600',
    iconBg: 'bg-zebu-50 dark:bg-zebu-900/20',
  },
};

export const getBrandClasses = (): BrandClasses =>
  brandClassMap[getTenantConfig().primaryColor] || brandClassMap.blue;
